import React, { type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { UserCheck, Pencil, Map, Loader2 } from "lucide-react";
import { UserAvatar } from "@/components/ui/user-avatar";
import { DataTable, type DataTableColumn, type DataTableServerPagination } from "@/components/ui/data-table";
import { TravelRequestBadge } from "./TravelRequestBadge";
import { UserRole } from "@/lib/auth/models/auth.model";
import { useI18n } from "@/hooks/useI18n";
import { cn, formatBudget, formatTravelDates } from "@/components/utils/utils";
import { DashboardTravelRequest } from "@/lib/models/dashboard";

interface TravelRequestsTableProps {
  travelRequests: DashboardTravelRequest[];
  userRole: UserRole | null;
  /** Click sulla riga: apre il dettaglio della richiesta. */
  onRowClick: (request: DashboardTravelRequest) => void;
  /** Apre il modale della richiesta in modifica. Assente = azione non disponibile. */
  onEditRequest?: (request: DashboardTravelRequest) => void;
  /** Riga per riga: se la modifica e' concessa. Assente = concessa ovunque. */
  canEditRequest?: (request: DashboardTravelRequest) => boolean;
  /**
   * Solo agency: la richiesta non e' su nessun membro (vedi `needsAgencyAssignment`).
   * L'azione di modifica diventa "Assegna" col pallino rosso, come nelle card.
   */
  needsAssignment?: (request: DashboardTravelRequest) => boolean;
  /** Le righe sono gia' la pagina corrente: pagina e totale li gestisce il chiamante. */
  serverPagination?: DataTableServerPagination;
  pageSize?: number;
  isLoading?: boolean;
  /** Vedi DataTable: rifa' l'animazione di entrata a ogni caricamento. */
  animationKey?: string | number;
  emptyState?: ReactNode;
  /**
   * Presente = colonna "Itinerario scelto" (vista completate): il titolo apre
   * l'anteprima dell'itinerario.
   */
  onViewChosenItinerary?: (itineraryId: string) => void;
  /** Itinerario la cui anteprima e' in caricamento. */
  chosenItineraryLoadingId?: string | null;
}

/** Travel request in tabella, con la stessa DataTable delle altre liste del planner. */
export const TravelRequestsTable: React.FC<TravelRequestsTableProps> = ({
  travelRequests,
  userRole,
  onRowClick,
  onEditRequest,
  canEditRequest,
  needsAssignment,
  serverPagination,
  pageSize,
  isLoading,
  animationKey,
  emptyState,
  onViewChosenItinerary,
  chosenItineraryLoadingId,
}) => {
  const { t } = useI18n();
  // Sotto la destinazione la controparte: il cliente vede chi segue la richiesta, chi ci
  // lavora vede il cliente. La colonna assegnatario la vedono tutti tranne l'advisor,
  // per cui sarebbe sempre se stesso.
  const isClient = userRole === "traveler";
  const showAdvisorColumn = userRole !== "travel_designer" && userRole !== "travel_advisor_member";
  const counterpartOf = (trip: DashboardTravelRequest) =>
    isClient
      ? trip.assignedAdvisor && {
          name: trip.assignedAdvisor.fullName,
          email: trip.assignedAdvisor.email,
          imageUrl: trip.assignedAdvisor.avatarUrl,
        }
      : { name: trip.contactFullName, email: trip.contactEmail, imageUrl: trip.contact.avatarUrl };

  const columns: DataTableColumn<DashboardTravelRequest>[] = [
    {
      id: "destination",
      header: t("travelRequest.tableDestination"),
      cell: (trip) => {
        const person = counterpartOf(trip);
        return (
        // max-w: con il layout automatico una destinazione lunga allargherebbe la colonna.
        <div className="min-w-0 max-w-[300px]">
          <div className="font-medium text-gray-900 truncate">
            {trip.destinations.length > 0 ? trip.destinations.join(", ") : t("travelRequest.destinationTBD")}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 min-w-0">
            {person ? (
              <>
                <UserAvatar fullName={person.name} email={person.email} imageUrl={person.imageUrl} size="xs" />
                <span className="text-sm text-gray-500 truncate">{person.name}</span>
              </>
            ) : (
              <span className="text-sm text-gray-400 truncate">{t("travelRequest.notAssigned")}</span>
            )}
          </div>
          {trip.departurePoint && (
            <div className="text-xs text-gray-400 truncate">
              {t("travelRequest.departureFrom")} {trip.departurePoint}
            </div>
          )}
        </div>
        );
      },
    },
    {
      id: "dates",
      header: t("travelRequest.tableDates"),
      // Le date non vanno mai a capo: se manca spazio scorre la tabella.
      headClassName: "whitespace-nowrap",
      cellClassName: "text-gray-600 whitespace-nowrap",
      cell: (trip) =>
        formatTravelDates(trip.travelStartDate, trip.travelEndDate, {
          fallbackText: String(t("travelRequest.datesToBeDefined")),
        }),
    },
    {
      id: "people",
      header: t("travelRequest.tablePeople"),
      headClassName: "w-[90px]",
      cellClassName: "text-gray-600",
      cell: (trip) => trip.totalGuests || "—",
    },
    {
      id: "budget",
      header: t("travelRequest.budget"),
      cellClassName: "text-gray-600",
      cell: (trip) => formatBudget(trip.budgetMin, trip.budgetMax),
    },
    ...(onViewChosenItinerary
      ? [{
          id: "chosenItinerary",
          header: t("requests.chosenItinerary"),
          cell: (trip: DashboardTravelRequest) => {
            const chosen = trip.chosenItinerary;
            if (!chosen) return <span className="text-sm text-gray-400">—</span>;
            const loading = chosenItineraryLoadingId === chosen.id;
            return (
              <button
                type="button"
                className="flex max-w-[220px] items-center gap-1.5 text-left text-sm font-medium text-gray-900 hover:underline disabled:opacity-60"
                // Il click sulla riga apre la richiesta: qui si apre l'itinerario.
                onClick={(e) => {
                  e.stopPropagation();
                  onViewChosenItinerary(chosen.id);
                }}
                disabled={loading}
                title={chosen.title ?? undefined}
              >
                {loading
                  ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                  : <Map className="h-4 w-4 shrink-0 text-gray-500" />}
                <span className="truncate">{chosen.title || t("itinerary.untitledItinerary")}</span>
              </button>
            );
          },
        }]
      : []),
    {
      id: "status",
      header: t("travelRequest.tableStatus"),
      // w-px + nowrap: la colonna e' larga quanto il badge piu' lungo della pagina,
      // non una misura fissa (layout automatico, vedi tableClassName).
      headClassName: "w-px whitespace-nowrap",
      cell: (trip) => <TravelRequestBadge travelRequest={trip} />,
    },
    ...(showAdvisorColumn
      ? [{
          id: "designer",
          header: t("travelRequest.designer"),
          cell: (trip: DashboardTravelRequest) => trip.assignedAdvisor ? (
            <div className="flex items-center gap-2 min-w-0">
              <UserAvatar
                firstName={trip.assignedAdvisor.firstName}
                lastName={trip.assignedAdvisor.lastName}
                email={trip.assignedAdvisor.email}
                imageUrl={trip.assignedAdvisor.avatarUrl}
                size="sm"
              />
              <div className="min-w-0 max-w-[200px]">
                <div className="font-medium text-gray-900 truncate">{trip.assignedAdvisor.fullName}</div>
                <div className="text-sm text-gray-500 truncate">{trip.assignedAdvisor.email}</div>
              </div>
            </div>
          ) : (
            <span className="text-sm text-gray-400">{t("travelRequest.notAssigned")}</span>
          ),
        }]
      : []),
  ];

  const renderEdit = (trip: DashboardTravelRequest) => {
    if (!onEditRequest || (canEditRequest && !canEditRequest(trip))) return null;
    const toAssign = !!needsAssignment?.(trip);
    const label = String(toAssign ? t("travelRequest.assign") : t("travelRequest.editRequest"));
    return (
      <div className="relative inline-flex">
        <Button
          variant="outline"
          size="icon"
          className={cn("h-8 w-8", toAssign && "border-red-300 text-red-600 hover:bg-red-50")}
          onClick={() => onEditRequest(trip)}
          title={label}
          aria-label={label}
        >
          {toAssign ? <UserCheck /> : <Pencil />}
        </Button>
        {toAssign && (
          <span
            className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white"
            aria-hidden
          />
        )}
      </div>
    );
  };

  return (
    <DataTable<DashboardTravelRequest>
      data={travelRequests}
      columns={columns}
      getRowId={(trip) => trip.id}
      onRowClick={onRowClick}
      actionsHeader={onEditRequest ? t("common.actions") : undefined}
      actionsHeadClassName="w-[90px]"
      renderActions={onEditRequest ? renderEdit : undefined}
      serverPagination={serverPagination}
      pageSize={pageSize}
      isLoading={isLoading}
      animationKey={animationKey}
      emptyState={emptyState}
      // table-auto: le colonne seguono il contenuto (lo stato si allarga solo per i
      // badge lunghi); i testi lunghi hanno un max-w e si troncano.
      tableClassName="min-w-[900px] table-auto"
    />
  );
};
